import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  CouponDiscountType,
  CouponDto,
  useCoupons,
  useCreateCoupon,
  useDeleteCoupon,
  useUpdateCoupon,
} from '@shared/api/hooks/useCoupons';

interface FormValues {
  code: string;
  discountType: CouponDiscountType;
  discountValue: number;
  maxUses?: number;
  expiresAt?: Dayjs;
  isActive: boolean;
}

export function CouponsPage(): React.ReactElement {
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form] = Form.useForm<FormValues>();

  const { data: coupons = [], isLoading, refetch, isFetching } = useCoupons();
  const createMutation = useCreateCoupon();
  const updateMutation = useUpdateCoupon();
  const deleteMutation = useDeleteCoupon();

  const openCreate = (): void => {
    setEditId(null);
    form.resetFields();
    form.setFieldsValue({ discountType: 'percentage', isActive: true });
    setModalOpen(true);
  };

  const openEdit = (c: CouponDto): void => {
    setEditId(c.id);
    form.setFieldsValue({
      code: c.code,
      discountType: c.discountType,
      discountValue: c.discountValue,
      maxUses: c.maxUses ?? undefined,
      expiresAt: c.expiresAt ? dayjs(c.expiresAt) : undefined,
      isActive: c.isActive,
    });
    setModalOpen(true);
  };

  const onSubmit = async (): Promise<void> => {
    const v = await form.validateFields();
    try {
      if (editId) {
        await updateMutation.mutateAsync({
          id: editId,
          input: {
            discountType: v.discountType,
            discountValue: v.discountValue,
            maxUses: v.maxUses,
            expiresAt: v.expiresAt ? v.expiresAt.toISOString() : undefined,
            isActive: v.isActive,
          },
        });
        message.success('Coupon aggiornato');
      } else {
        await createMutation.mutateAsync({
          code: v.code.toUpperCase().trim(),
          discountType: v.discountType,
          discountValue: v.discountValue,
          maxUses: v.maxUses,
          expiresAt: v.expiresAt ? v.expiresAt.toISOString() : undefined,
          isActive: v.isActive,
        });
        message.success('Coupon creato');
      }
      setModalOpen(false);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Errore di salvataggio'));
    }
  };

  const onDelete = async (id: string): Promise<void> => {
    try {
      await deleteMutation.mutateAsync(id);
      message.success('Coupon eliminato');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message ?? 'Errore eliminazione');
    }
  };

  return (
    <>
      {/* F8: i coupon sono confluiti nelle regole di sconto. La pagina resta in
          sola gestione dello storico — la creazione e' chiusa lato API. */}
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="I coupon sono stati sostituiti dalle regole di sconto"
        description={
          <>
            Non si creano piu nuovi coupon da qui: usa <b>Sconti store</b> e crea una regola con
            attivazione <b>Con codice</b>. Quelli esistenti sono gia stati migrati e continuano a
            funzionare; questa pagina resta per consultarli e disattivarli.
          </>
        }
      />
    <Card
      title={
        <Space>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Coupon
          </Typography.Title>
          <Tag>{coupons.length}</Tag>
        </Space>
      }
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            Aggiorna
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Nuovo coupon
          </Button>
        </Space>
      }
    >
      <Table<CouponDto>
        rowKey="id"
        loading={isLoading}
        dataSource={coupons}
        pagination={false}
        scroll={{ x: 'max-content' }}
        columns={[
          {
            title: 'Codice',
            dataIndex: 'code',
            render: (v: string) => <Typography.Text code>{v}</Typography.Text>,
          },
          {
            title: 'Sconto',
            render: (_v, r) =>
              r.discountType === 'percentage' ? `${r.discountValue}%` : `${r.discountValue} EUR`,
          },
          {
            title: 'Tipo',
            dataIndex: 'discountType',
            render: (v: CouponDiscountType) => (
              <Tag color={v === 'percentage' ? 'blue' : 'purple'}>
                {v === 'percentage' ? 'Percentuale' : 'Importo fisso'}
              </Tag>
            ),
          },
          {
            title: 'Utilizzi',
            render: (_v, r) =>
              r.maxUses === null
                ? `${r.usedCount} / ∞`
                : `${r.usedCount} / ${r.maxUses}`,
          },
          {
            title: 'Scadenza',
            dataIndex: 'expiresAt',
            render: (v: string | null) =>
              v ? dayjs(v).format('DD/MM/YYYY') : <Typography.Text type="secondary">—</Typography.Text>,
          },
          {
            title: 'Stato',
            dataIndex: 'isActive',
            render: (v: boolean) =>
              v ? <Tag color="green">Attivo</Tag> : <Tag color="default">Disattivato</Tag>,
          },
          {
            title: '',
            width: 100,
            align: 'right',
            render: (_v, r) => (
              <Space>
                <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
                <Popconfirm
                  title="Eliminare coupon?"
                  okText="Elimina"
                  cancelText="Annulla"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => onDelete(r.id)}
                >
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title={editId ? 'Modifica coupon' : 'Nuovo coupon'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={onSubmit}
        okText={editId ? 'Salva' : 'Crea'}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        destroyOnHidden
        width={520}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="code"
            label="Codice (immutabile dopo creazione)"
            rules={[
              { required: true, min: 3, max: 30 },
              { pattern: /^[A-Z0-9_-]+$/, message: 'Solo maiuscole alfanumeriche + - _' },
            ]}
          >
            <Input
              disabled={editId !== null}
              placeholder="ESTATE2026"
              onChange={(e) => form.setFieldValue('code', e.target.value.toUpperCase())}
            />
          </Form.Item>
          <Space.Compact style={{ width: '100%' }}>
            <Form.Item
              name="discountType"
              label="Tipo sconto"
              rules={[{ required: true }]}
              style={{ flex: 1, marginRight: 8 }}
            >
              <Select
                options={[
                  { value: 'percentage', label: 'Percentuale' },
                  { value: 'fixed', label: 'Importo fisso (EUR)' },
                ]}
              />
            </Form.Item>
            <Form.Item
              name="discountValue"
              label="Valore"
              rules={[{ required: true, type: 'number', min: 0.01 }]}
              style={{ flex: 1 }}
            >
              <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
            </Form.Item>
          </Space.Compact>
          <Space.Compact style={{ width: '100%' }}>
            <Form.Item
              name="maxUses"
              label="Utilizzi massimi (vuoto = illimitati)"
              style={{ flex: 1, marginRight: 8 }}
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="expiresAt" label="Scadenza" style={{ flex: 1 }}>
              <DatePicker
                format="DD/MM/YYYY"
                disabledDate={(d) => d.isBefore(dayjs().startOf('day'))}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Space.Compact>
          <Form.Item name="isActive" label="Attivo" valuePropName="checked">
            <Switch checkedChildren="Sì" unCheckedChildren="No" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
    </>
  );
}
